import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { Form, Formik } from "formik";
import { FormattedMessage } from "react-intl";
import { Button } from "app/components/ui/Button/Button";
import RelativeLink from "app/pages/.shared/RelativeLink";
import FacebookContainer from "app/pages/Auth/Facebook/FacebookContainer.jsx";
import AlertMessage from "app/components/ui/AlertMessage/AlertMessage";
import { ALERT_TYPE } from "app/constants";
import { getParentPath } from "app/utils/routerUtils";
import { FormikTextField } from "app/components/form/FormikTextField/FormikTextField";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import { object, string } from "yup";
import TimerCountdown from "app/pages/.shared/TimerCountdown/TimerCountdown";
import useShowPassword from "app/utils/hooks/useShowPassword";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useCallback } from "react";
import SignInWithGoogleButtonContainer from "app/pages/Auth/Google/SignInWithGoogleButtonContainer";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import "./Login.scss";

const validationSchema = object().shape({
	email: string().required({ id: "error.email.required" }).email({ id: "error.email.format" }),
	password: string().required({ id: "error.password.required" }),
});

const Login = ({
	email,
	handleSubmit = () => {},
	facebookOnSuccess = () => {},
	showTopFacebook = true,
	homeRedirection = true,
	footer = null,
	canLoginAt,
	setCanLoginAt = () => {},
}) => {
	const location = useLocation();
	const parentPath = getParentPath(location.pathname) || "/home";
	const now = Date.now();
	const [icon, showPassword] = useShowPassword();
	const { track } = useAnalytics();

	const handleCheckClick = useCallback(() => {
		track("signin_click_email");
	}, []);

	return (
		<div className="login">
			<div className="login__main">
				<Formik
					initialValues={{
						email: email,
						password: "",
					}}
					onSubmit={handleSubmit}
					validateOnChange={false}
					validateOnBlur={false}
					validationSchema={validationSchema}
				>
					{({
						errors = {},
						isSubmitting,
						setSubmitting,
						setStatus,
						setErrors,
						submitCount,
					}) => {
						return (
							<Form
								noValidate
								className="login__form"
								onChange={() => {
									setStatus({});
								}}
							>
								<FormErrorMessages
									errors={errors}
									isSubmitting={isSubmitting}
									submitCount={submitCount}
								/>

								<div className="login__form-fields">
									<FormikTextField
										type="email"
										name="email"
										id="auth-email"
										data-testid="email-input"
										label={<FormattedMessage id="auth.email.label" />}
									/>

									<FormikTextField
										type={showPassword ? "text" : "password"}
										name="password"
										id="auth-password"
										data-testid="password-input"
										label={<FormattedMessage id="auth.password.label" />}
										className="login__password"
										note={icon}
									/>

									<div className="login__forgotten-password">
										<RelativeLink
											to={{
												pathname: `${
													homeRedirection ? "/home" : parentPath
												}/resetpassword`,
											}}
											data-testid="forgot-password-button"
										>
											<Typography
												variant={TYPOGRAPHY_VARIANTS.SMALL}
												component="span"
											>
												<FormattedMessage id="auth.password.forgotten.label" />
											</Typography>
										</RelativeLink>
									</div>
								</div>

								<div className="login__action">
									{!canLoginAt && errors._error && (
										<AlertMessage
											alertType={ALERT_TYPE.ERROR}
											message={
												<div className="login__messages-error">
													<FormattedMessage id={errors._error.id} />
												</div>
											}
										/>
									)}
									{canLoginAt && canLoginAt > now && (
										<AlertMessage
											alertType={ALERT_TYPE.ERROR}
											message={
												<div className="login__messages-error">
													<FormattedMessage
														id="error.block.login"
														values={{
															timer: (
																<TimerCountdown
																	date={canLoginAt}
																	onComplete={() => {
																		setCanLoginAt(undefined);
																		setStatus({});
																		setErrors({});
																	}}
																/>
															),
														}}
													/>
												</div>
											}
										/>
									)}

									<Button
										variant="primary"
										size="lg"
										type="submit"
										fullWidth
										className="login__button"
										loading={isSubmitting}
										data-testid="login-button"
										disabled={Boolean(canLoginAt) && canLoginAt > now}
										onClick={handleCheckClick}
									>
										<FormattedMessage id="auth.signin.button.label" />
									</Button>
								</div>

								<Typography
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									className="login__separator"
								>
									<FormattedMessage id="general.ou" />
								</Typography>

								<div className="login__social-buttons">
									<div className="login__google">
										<SignInWithGoogleButtonContainer
											uxMode={"signin"}
											onAuthSuccess={facebookOnSuccess}
											onAuthError={() => {
												setErrors({ _error: { id: "error.generic" } });
												setSubmitting(false);
											}}
										/>
									</div>

									{showTopFacebook && (
										<div
											className="login__facebook"
											data-testid="auth-facebook-top"
										>
											<FacebookContainer
												onSuccess={facebookOnSuccess}
												onError={() => {
													setErrors({ _error: { id: "error.generic" } });
													setSubmitting(false);
												}}
											/>
										</div>
									)}
								</div>
							</Form>
						);
					}}
				</Formik>
				{footer && footer.props?.children && <div className="login__footer">{footer}</div>}
			</div>
		</div>
	);
};

Login.propTypes = {
	email: PropTypes.string,
	handleSubmit: PropTypes.func,
	facebookOnSuccess: PropTypes.func,
	showTopFacebook: PropTypes.bool,
	homeRedirection: PropTypes.bool,
	footer: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.bool]),
	canLoginAt: PropTypes.number,
	setCanLoginAt: PropTypes.func,
};

export default Login;
